<template>
  <div class="card mb-2">
    <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
      <b-form class="search-form" @submit.prevent="onSearch">
        <div class="d-flex">
          <div class="row align-items-center">
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="invoice_no"/>
            </div>
            <div class="input-wrapper">
              <customer-select-input-widget @select="onCustomerSelect"
                                            :model="searchModel"
                                            :label="false" is-search
                                            :options="{placeholder: $t('Customer')}"
                                            attribute="customer_uuid"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('first_name')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="first_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('last_name')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="last_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('company_name')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="company_name"/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="min_amount" type="number"
                            :min="Infinity"/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="max_amount" type="number"
                            :min="Infinity"/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="batch_id"/>
            </div>
            <div class="input-wrapper">
              <b-form-group class="">
                <date-range-picker
                  class="d-flex w-100"
                  @update="onPaymentDateRangeApply"
                  showWeekNumbers
                  showDropdowns
                  ref="picker"
                  :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY HH:mm:ss' }"
                  v-model="paymentDateRange">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    <span v-if="!picker.startDate" class="text-muted">
                      {{ $t('Date') }}
                    </span>
                    <span style="font-size: 10px" v-else>
                      {{ picker.startDate | toDate }} - {{ picker.endDate | toDate }}
                    </span>
                  </template>
                </date-range-picker>
              </b-form-group>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="sale_no"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="batch_status" value-field="value"
                            :label="false" :placeholder="true" :options="{placeholder: $t('Batch Status')}"
                            display-field="${item.key}" type="select2" :items="batchPaymentStatuses"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="payment_method" value-field="value"
                            :label="false" :placeholder="true" :options="{placeholder: $t('Method')}"
                            display-field="${item.key}" type="select2" :items="paymentMethods"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="payment_type" value-field="value"
                            :label="false" :placeholder="true" :options="{placeholder: $t('Type')}"
                            display-field="${item.key}" type="select2" :items="paymentTypes"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="invoice_type" value-field="key"
                            :label="false" :placeholder="true" :options="{placeholder: $t('Invoice Type')}"
                            display-field="${item.text}" type="select2" :items="invoiceTypes"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="bank_account" value-field="value"
                            :label="false" :placeholder="true" :options="{placeholder: $t('Bank Account')}"
                            display-field="${item.key}" type="select2" :items="paymentBankAccounts"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="reference_number" :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper">
              <currency-select-input-widget attribute="currency_uuid" :model="searchModel" :label="false" :placeholder="true"
                                             :options="{placeholder: $t('Currency')}"/>
            </div>
            <div class="input-wrapper" v-if="vendorBuyerGroupAccountsEnabled">
              <input-widget :model="searchModel" attribute="buyer_group_account" :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper" v-if="branches.rows.length > 1">
              <input-widget type="select2" :model="searchModel" attribute="branch_uuids" :label="false" multiple
                 :options="{placeholder: $t('Branch')}" :items="branches.rows" value-field="uuid"
                 display-field="${item.name}"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" type="checkbox" attribute="reconciled"/>
            </div>
          </div>
          <div class="search-btn-wrapper">
            <b-button variant="primary" @click.prevent="onResetClick">
              <font-awesome-icon icon="undo" class="icon"/>
              {{ $t('Reset') }}
            </b-button>
            <b-button type="submit" variant="primary" class="ml-2">
              <font-awesome-icon icon="search" class="icon"/>
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import DateRangePicker from 'vue2-daterange-picker'
  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('invoicePayments');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState: mapStateBranch} = createNamespacedHelpers('setup/branch');

  export default {
    name: "InvoicePaymentSearch",
    components: {CurrencySelectInputWidget, CustomerSelectInputWidget, InputWidget, DateRangePicker},
    props: {
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
    },
    computed: {
      ...mapState({
        searchModel: state => state.tableData[state.tableSource].searchModel,
      }),
      ...mapStateBranch(['branches']),
      ...mapStateGlobal({
        batchPaymentStatuses: state => state.batchPaymentStatuses,
        paymentMethods: state => state.paymentMethods,
        paymentTypes: state => state.paymentTypes,
        paymentBankAccounts: state => state.paymentBankAccounts,
        invoiceTypes: state => state.invoiceTypes,
        vendorBuyerGroupAccountsEnabled: state => state.vendorBuyerGroupAccountsEnabled,
      }),
    },
    data() {
      return {
        paymentDateRange: {startDate: null, endDate: null},
      }
    },
    methods: {
      ...mapActions(['getTableData', 'updateSearchModel', 'resetTable', 'resetTableDataPaginationPage']),
      async onSearch() {
        await this.updateSearchModel(this.searchModel);
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      onResetClick() {
        this.paymentDateRange = {startDate: null, endDate: null};
        this.resetTable();
      },
      async onCustomerSelect(customer) {
        this.updateSearchModel({searchModel: {customer}});
      },
      onPaymentDateRangeApply() {
        this.searchModel.paymentDateRange = this.paymentDateRange;
      },
    },
    mounted() {
      if (this.searchModel && this.searchModel.paymentDateRange) {
        this.paymentDateRange = this.searchModel.paymentDateRange;
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>
